<script setup lang="ts">
import { ref, computed } from 'vue';
import { ResourceService } from '../../api/resource/resourceService';
import { ResourceAbort } from '../../api/resource/aborts/resourceAborts';
import { type File } from '../../api/resource/types/file';
import { ButtonSeverity } from '../../presets/button/types/buttonServerity';
import BaseButton from '../BaseButton/BaseButton.vue';
import FilesList from './components/FilesList/FilesList.vue';

const MAX_CHUNK_SIZE = 100_000_000; // ~100MB
const MAX_FILE_SIZE = 3865470566;

defineProps({
  uploadDialogVisible: {
    default: false,
    type: Boolean,
  },
});

const emit = defineEmits<{
  close: [boolean];
  dataUploaded: [];
}>();

const uploadFiles = ref<File[]>([]);
const uploadedFiles = ref<File[]>([]);
const failedUploadFiles = ref<File[]>([]);
const uploadFailed = ref<boolean>(false);
const dataUploaded = ref<boolean>(false);
const uploading = ref<boolean>(false);
const isFilesLoading = ref<boolean>(false);
const progress = ref<number>(0);
const uploadAbortController = ref(new AbortController());

const onClose = (): void => {
  uploadAbortController.value.abort();
  uploadedFiles.value.forEach((file) => URL.revokeObjectURL(file.objectURL));
  uploadedFiles.value = [];
  uploadFiles.value = [];
  uploadFiles.value.forEach((file) => URL.revokeObjectURL(file.objectURL));
  failedUploadFiles.value = [];
  failedUploadFiles.value.forEach((file) => URL.revokeObjectURL(file.objectURL));
  emit('close', dataUploaded.value);
};

const confirmAbort = (): void => {
  if (confirm('Czy na pewno chcesz przerwać przesyłanie plików i wyjść?')) {
    onClose();
  }
};

const handleFileSelect = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  if (!input.files?.length) return;

  const files = Array.from(input.files).filter((file) => {
    if (file.size > MAX_FILE_SIZE) {
      alert('Plik jest za duży.');
      return false;
    }
    return file.type.includes('image') || file.type.includes('video');
  });

  const processedFiles = files.map((file) => {
    const processedFile = file as unknown as File;
    if (file.type.includes('image')) {
      processedFile.objectURL = URL.createObjectURL(file);
    }
    return processedFile;
  });

  uploadFiles.value = [...uploadFiles.value, ...processedFiles];
  isFilesLoading.value = false;
  input.value = '';
};

const onUpload = async (): Promise<void> => {
  if (!uploadFiles.value.length) return;

  const chunkSize = MAX_CHUNK_SIZE;
  uploading.value = true;
  let filesToSend: File[] = [];
  progress.value = 0;

  try {
    const totalFilesSize = uploadFiles.value.reduce((acc, file) => acc + file.size, 0);
    let uploadRunningTotal = 0;

    for (let i = 0; i < uploadFiles.value.length; i++) {
      const file = uploadFiles.value[i];
      const fileSize = file?.size ?? 0;

      if (fileSize > chunkSize) {
        dataUploaded.value = true;
        await ResourceService.uploadResource({
          files: [file as unknown as File],
          manualAbort: uploadAbortController.value.signal,
        });
        uploadRunningTotal += fileSize;
        uploadedFiles.value.push(file as unknown as File);
        progress.value = Math.round((uploadRunningTotal / totalFilesSize) * 100);
        continue;
      }

      filesToSend.push(file as unknown as File);

      if (filesToSend.reduce((acc, f) => acc + f.size, 0) >= chunkSize || i === uploadFiles.value.length - 1) {
        dataUploaded.value = true;
        await ResourceService.uploadResource({
          files: filesToSend,
          manualAbort: uploadAbortController.value.signal,
        });
        uploadedFiles.value.push(...filesToSend);
        uploadRunningTotal += filesToSend.reduce((acc, f) => acc + f.size, 0);
        progress.value = Math.round((uploadRunningTotal / totalFilesSize) * 100);
        filesToSend = [];
      }
    }

    uploadFiles.value = [];
    uploading.value = false;
    emit('dataUploaded');
  } catch (error) {
    if (error === ResourceAbort.timeout || error === ResourceAbort.network || (error as Error)?.message === '500') {
      failedUploadFiles.value = [...uploadFiles.value];
      uploadFiles.value = [];
      uploadFailed.value = true;
      uploading.value = false;
      dataUploaded.value = true;
    }
  }
};

const onResumeUpload = async (): Promise<void> => {
  uploadFiles.value = [...failedUploadFiles.value];
  failedUploadFiles.value = [];
  await onUpload();
};

const onRemoveUploadedFile = (file: File): void => {
  const index = uploadedFiles.value.findIndex((uploadedFile) => uploadedFile.name === file.name);
  if (index !== -1) {
    uploadedFiles.value.splice(index, 1);
  }
};

const onRemoveFailedFile = (file: File): void => {
  const index = failedUploadFiles.value.findIndex((failedUpload) => failedUpload.name === file.name);
  if (index !== -1) {
    failedUploadFiles.value.splice(index, 1);
  }
};

const onRemoveFile = (file: File): void => {
  const index = uploadFiles.value.findIndex((uploadFile) => uploadFile.name === file.name);
  if (index !== -1) {
    uploadFiles.value.splice(index, 1);
  }
};

const onClear = (): void => {
  uploadedFiles.value.forEach((file) => URL.revokeObjectURL(file.objectURL));
  uploadFiles.value.forEach((file) => URL.revokeObjectURL(file.objectURL));
  failedUploadFiles.value.forEach((file) => URL.revokeObjectURL(file.objectURL));
  failedUploadFiles.value = [];
  uploadedFiles.value = [];
  uploadFiles.value = [];
  progress.value = 0;
};

const hasFiles = computed(
  () => uploadFiles.value.length > 0 || uploadedFiles.value.length > 0 || failedUploadFiles.value.length > 0,
);
</script>

<template>
  <div
    v-if="uploadDialogVisible"
    class="file-upload-dialog"
  >
    <div
      class="file-upload-dialog__overlay"
      @click="uploading ? confirmAbort() : onClose()"
    ></div>
    <div class="file-upload-dialog__content">
      <div class="file-upload-dialog__header">
        <h2 class="file-upload-dialog__title">Wybierz pliki</h2>
        <button
          class="file-upload-dialog__close-button"
          @click="uploading ? confirmAbort() : onClose()"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="file-upload-dialog__body">
        <div class="file-upload-dialog__actions">
          <div class="file-upload-dialog__main-actions">
            <label class="file-upload-dialog__button">
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                :disabled="isFilesLoading || uploading"
                class="file-upload-dialog__file-input"
                @change="handleFileSelect"
              />
              <i class="pi pi-plus"></i>
              Wybierz
            </label>
            <BaseButton
              v-if="failedUploadFiles.length > 0"
              :icon="'pi pi-replay'"
              :button-text="'Wznów'"
              :severity="ButtonSeverity.tertiary"
              :disabled="isFilesLoading || uploading"
              class="file-upload-dialog__button"
              @clicked="onResumeUpload"
            />
            <BaseButton
              :icon="'pi pi-upload'"
              :button-text="'Wyślij'"
              :loading="uploading"
              :severity="ButtonSeverity.primary"
              :disabled="uploadFiles.length === 0 || isFilesLoading"
              class="file-upload-dialog__button"
              @clicked="onUpload"
            />
          </div>
          <BaseButton
            :icon="'pi pi-times'"
            :button-text="'Wyczyść'"
            :severity="ButtonSeverity.ghost"
            :disabled="!hasFiles || isFilesLoading || uploading"
            class="file-upload-dialog__button"
            @clicked="onClear"
          />
        </div>

        <div class="file-upload-dialog__progress">
          <div
            class="file-upload-dialog__progress-bar"
            :style="{ width: `${progress}%` }"
          ></div>
        </div>

        <div
          v-if="!isFilesLoading && !hasFiles"
          class="file-upload-dialog__drop-zone"
          @dragover.prevent
          @drop.prevent="handleFileSelect($event)"
        >
          <p class="file-upload-dialog__drop-zone-text">Przeciągnij i upuść pliki tutaj lub użyj przycisku "Wybierz"</p>
        </div>

        <div
          v-if="isFilesLoading"
          class="file-upload-dialog__loading"
        >
          <div class="file-upload-dialog__spinner"></div>
        </div>

        <div
          v-if="!isFilesLoading && hasFiles"
          class="file-upload-dialog__files-list"
        >
          <FilesList
            :chip-color="'bg-primaryGold'"
            :chip-label="'Sukces'"
            :files="uploadedFiles"
            :delete-disabled="uploading"
            class="file-upload-dialog__files-list-item"
            @remove-file="onRemoveUploadedFile"
          />
          <FilesList
            :chip-color="'bg-red-400'"
            :chip-label="'Błąd'"
            :files="failedUploadFiles"
            :delete-disabled="uploading"
            class="file-upload-dialog__files-list-item"
            @remove-file="onRemoveFailedFile"
          />
          <FilesList
            :chip-color="'bg-gray-400'"
            :chip-label="uploading ? 'Wysyłanie...' : 'Gotowy do wysłania'"
            :files="uploadFiles"
            :delete-disabled="uploading"
            class="file-upload-dialog__files-list-item"
            @remove-file="onRemoveFile"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.file-upload-dialog {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  &__overlay {
    position: absolute;
    inset: 0;
    background-color: rgba($black, 0.5);
    backdrop-filter: blur(4px);
  }

  &__content {
    position: relative;
    width: 90%;
    max-width: 80rem;
    max-height: 90vh;
    background-color: $white-200;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba($black-100, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__header {
    padding: 2rem 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid $brown-300;
    background-color: $white-200;
  }

  &__title {
    font-size: 2.25rem;
    color: $brown-200;
    font-weight: 600;
    margin: 0;

    @media (min-width: $media-min-tablet) {
      font-size: 3rem;
    }
  }

  &__close-button {
    background: none;
    border: none;
    color: $brown-200;
    font-size: 2rem;
    cursor: pointer;
    padding: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      color: $brown-100;
      transform: scale(1.1);
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;

    @media (min-width: $media-min-tablet) {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  &__main-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media (min-width: $media-min-tablet) {
      flex-direction: row;
    }
  }

  &__button {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    border-radius: 0.5rem;
    font-size: 1.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba($black-100, 0.1);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba($black-100, 0.15);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba($black-100, 0.1);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  &__file-input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  }

  &__progress {
    height: 0.5rem;
    background-color: $brown-300;
    border-radius: 0.25rem;
    overflow: hidden;
    margin-bottom: 2rem;
  }

  &__progress-bar {
    height: 100%;
    background-color: $brown-100;
    transition: width 0.3s ease;
  }

  &__drop-zone {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 12rem;
    border: 4px dashed $brown-200;
    border-radius: 0.5rem;
    background-color: $white;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      border-color: $brown-100;
      background-color: $white-200;
    }

    @media (min-width: $media-min-tablet) {
      min-height: 24rem;
    }
  }

  &__drop-zone-text {
    color: $black;
    font-size: 1.25rem;
    text-align: center;
    padding: 0 2rem;

    @media (min-width: $media-min-tablet) {
      font-size: 1.5rem;
    }
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 12rem;
    padding: 2rem;

    @media (min-width: $media-min-tablet) {
      min-height: 24rem;
    }
  }

  &__spinner {
    width: 8rem;
    height: 8rem;
    border: 3px solid $brown-300;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @media (min-width: $media-min-tablet) {
      width: 16rem;
      height: 16rem;
    }
  }

  &__files-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__files-list-item {
    background-color: $white;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba($black-100, 0.05);
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 4px 8px rgba($black-100, 0.1);
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
